<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaCard from '../../base/GaiaCard.vue';
import GaiaSpinner from '../../base/GaiaSpinner.vue';
import GaiaStatusChip, { type GaiaChipTone } from '../../base/GaiaStatusChip.vue';
import GaiaText from '../../base/GaiaText.vue';

const props = defineProps({
  /** The provider's name — "Google Drive", "Slack", "Jira". */
  title: {
    type: String,
    required: true,
  },
  /** The status word beside the dot: "Connected", "Finish setup", "Not Connected". */
  statusLabel: {
    type: String,
    default: undefined,
  },
  tone: {
    type: String as PropType<GaiaChipTone>,
    default: 'neutral',
  },
  /** One sentence saying what the state means, or what connecting will do. */
  message: {
    type: String,
    default: undefined,
  },
  /** Veils the card while its own status is in flight. */
  loading: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <!-- One provider connection on the Integrations page: name, status, what the
       state means, the odd detail line, and the actions that change it. Every
       card in the grid carries the same four parts in the same order, so the
       shape lives here rather than being retyped per provider. -->
  <gaia-card class="gaia-integration-card">
    <template #header>
      <gaia-text size="subtitle" weight="semibold" tone="ink">{{ props.title }}</gaia-text>
    </template>

    <gaia-spinner :show="props.loading">
      <n-flex vertical :size="12" :wrap="false" class="gaia-integration-card__body">
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-text variant="label" size="micro" tone="faint">Status</gaia-text>
          <gaia-status-chip
            v-if="props.statusLabel"
            :tone="props.tone"
            :style="{ marginLeft: 'auto' }"
          >
            {{ props.statusLabel }}
          </gaia-status-chip>
        </n-flex>

        <gaia-text v-if="props.message" size="body" tone="text" :style="{ lineHeight: 1.5 }">
          {{ props.message }}
        </gaia-text>

        <!-- Connection details: authorized at, workspace, site. -->
        <n-flex v-if="$slots.meta" vertical :size="2" :wrap="false">
          <slot name="meta" />
        </n-flex>

        <slot />

        <!-- `margin-top: auto` rather than a spacer: the grid stretches every
             card to the tallest one, and the action rows have to line up. -->
        <n-flex
          v-if="$slots.actions"
          align="center"
          :size="8"
          :style="{ marginTop: 'auto', paddingTop: '4px' }"
        >
          <slot name="actions" />
        </n-flex>
      </n-flex>
    </gaia-spinner>
  </gaia-card>
</template>

<style scoped>
/* The grid hands every card the full row height; naive's card, its content
   element (`.n-card-content` — one dash, not two) and the spin wrappers are
   plain blocks, so without these the body cannot stretch and the action row
   has nothing to push against. Not expressible as a theme token. */
.gaia-integration-card,
.gaia-integration-card :deep(.n-card-content),
.gaia-integration-card :deep(.n-spin-container),
.gaia-integration-card :deep(.n-spin-content) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.gaia-integration-card__body {
  flex: 1;
}
</style>
